import { z } from 'zod';
export const MESSAGE_MAX_LENGTH = 4000;
/**
 * Mentions travel as `<@userId>` so they survive renames; clients show and type them as @Ad Soyad.
 * `@herkes` notifies everyone, but only when an admin writes it (design §11).
 */
export const MENTION_PATTERN = /<@([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})>/gi;
const EVERYONE_PATTERN = /(^|[^\p{L}\p{N}_])@herkes(?![\p{L}\p{N}_])/u;
const MAX_MENTIONS = 50;
/** Who a message mentions; code (fenced or inline) never mentions anyone. */
export function parseMentions(content) {
    const prose = content.replace(/```[\s\S]*?(```|$)/g, ' ').replace(/`[^`\n]*`/g, ' ');
    const userIds = [...new Set(Array.from(prose.matchAll(MENTION_PATTERN), (m) => m[1].toLowerCase()))].slice(0, MAX_MENTIONS);
    return { userIds, everyone: EVERYONE_PATTERN.test(prose) };
}
const URL_PATTERN = /https?:\/\/[^\s<>"'`)\]]+[^\s<>"'`)\].,;:!?]/gi;
/** Every link outside code, once each, in order. */
export function findLinks(content) {
    const prose = content.replace(/```[\s\S]*?(```|$)/g, ' ').replace(/`[^`\n]*`/g, ' ');
    return [...new Set(prose.match(URL_PATTERN) ?? [])];
}
/** Links that get a preview: the first three outside code. */
export function extractLinks(content) {
    return findLinks(content).slice(0, 3);
}
export const MESSAGE_PAGE_MAX = 100;
export const MAX_ATTACHMENTS = 10;
/** Cloudflare in front of the API accepts at most 100 MB per request. */
export const MAX_UPLOAD_BYTES = 95 * 1024 * 1024;
const content = z.string().max(MESSAGE_MAX_LENGTH);
export const SendMessageRequest = z
    .object({
    content: content.default(''),
    /** Chosen by the client before sending; a retry with the same id returns the first message. */
    clientId: z.string().min(1).max(64),
    replyToId: z.uuid().optional(),
    /** A reply in the thread under this channel message (docs/plans/2026-11-04-threads-design.md). */
    threadRootId: z.uuid().optional(),
    /** Uploaded first with POST /channels/:id/attachments. */
    attachmentIds: z.array(z.uuid()).max(MAX_ATTACHMENTS).default([]),
})
    .refine((body) => body.content.trim().length > 0 || body.attachmentIds.length > 0, { message: 'Message is empty', path: ['content'] });
/** Text may only be emptied when the message has attachments (checked by the server). */
export const EditMessageRequest = z.object({ content });
/** `before` pages back through history, `after` catches up after a reconnect; never both. */
export const MessageQuery = z
    .object({
    before: z.uuid().optional(),
    after: z.uuid().optional(),
    limit: z.coerce.number().int().min(1).max(MESSAGE_PAGE_MAX).default(50),
})
    .refine((query) => !(query.before && query.after), { message: 'Use before or after, not both' });
/** A single emoji or a `:shortcode:`, without whitespace. */
export const Emoji = z.string().min(1).max(64).regex(/^\S+$/u);
/** GET /search: words and/or filters; newest first, 25 at a time. */
export const SearchQuery = z
    .object({
    q: z.string().trim().max(200).default(''),
    /** Author */
    from: z.uuid().optional(),
    /** Channel (or DM) */
    in: z.uuid().optional(),
    has: z.enum(['file', 'image']).optional(),
    after: z.iso.date().optional(),
    before: z.iso.date().optional(),
    /** `nextCursor` of the previous page */
    cursor: z.uuid().optional(),
})
    .refine((query) => query.q.length > 0 || query.from || query.in || query.has, { message: 'Search for words or pick a filter' });
/** GET /channels/:id/media: what was shared in a channel, newest first (photos and videos, other files, or links). */
export const ChannelMediaQuery = z.object({
    kind: z.enum(['media', 'files', 'links']).default('media'),
    /** `nextCursor` of the previous page */
    cursor: z.uuid().optional(),
});
export const MarkReadRequest = z.object({ messageId: z.uuid() });
export const OpenDmRequest = z.object({ userId: z.uuid() });
//# sourceMappingURL=messages.js.map